import { Button, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Main } from "@/app/layout/Main";
import { QueryState } from "@/shared/ui/QueryState";
import {
  useDeleteStudentGroup,
  useStudentGroupQuery,
  useUpdateStudentGroup,
} from "../api/queries";
import { StudentGroupForm } from "../ui/student-groups/StudentGroupForm";

export function StudentGroupEditPage() {
  const nav = useNavigate({
    from: "/app/master/student-groups/$id/edit",
  });
  const { id } = useParams({
    from: "/app/master/student-groups/$id/edit",
  });
  const { data, ...result } = useStudentGroupQuery(id);
  const updateStudentGroup = useUpdateStudentGroup(id);
  const deleteStudentGroup = useDeleteStudentGroup();

  const group = data;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ກຸ່ມນັກຮຽນ", to: "/app/master/student-groups" },
            { label: "ແກ້ໄຂ" },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="flex items-center font-bold text-2xl tracking-tight">
              ແກ້ໄຂກຸ່ມນັກຮຽນ
            </h2>
            <p className="text-muted-foreground">ປັບປຸງລາຍລະອຽດຂອງກຸ່ມນັກຮຽນ.</p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="destructive"
              onClick={async () => {
                const ok = await import("@devhop/ui").then((m) =>
                  m.confirm({
                    title: "ລຶບກຸ່ມນັກຮຽນ",
                    description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບກຸ່ມນັກຮຽນນີ້?",
                    actionText: "ລຶບ",
                    ActionProps: {
                      variant: "destructive",
                    },
                  }),
                );
                if (!ok) return;

                try {
                  await toast.promise(deleteStudentGroup.run(id), {
                    loading: "ກຳລັງລຶບ...",
                    success: () => {
                      nav({ to: "/app/master/student-groups" });
                      return "ລຶບກຸ່ມນັກຮຽນສຳເລັດ";
                    },
                    error: "ລຶບກຸ່ມນັກຮຽນລົ້ມເຫຼວ",
                  });
                } catch (error) {
                  // Error handling is done by toast.promise
                }
              }}
            >
              ລຶບ
            </Button>
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/master/student-groups" })}
            >
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button>
          </div>
        </div>

        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດກຸ່ມນັກຮຽນ"
          description="ກຳລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!group ? null : (
            <div className="mt-6 rounded-xl border bg-card p-6">
              <StudentGroupForm
                initialValues={{
                  name: group.name,
                  code: group.code,
                  description: group.description || "",
                  order: group.order ?? undefined,
                  status: group.status,
                }}
                onSubmit={async (vals) => {
                  try {
                    await updateStudentGroup.mutateAsync(vals);

                    nav({ to: "/app/master/student-groups" });
                  } catch (error) {
                    // Error handling is done by the mutation
                  }
                }}
                submitting={updateStudentGroup.isPending}
              />
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}
